<template>
  <div class="message-container">
    <div class="header">
      <div :class="['comment-author', { removed: isRemovedParticipant }]">
        <Avatar
          :name="createdByMetaData.name"
          :thumb-url="createdByMetaData.thumbUrl"
          :size="AvatarSize.Small"
          :deleted-at="createdByMetaData.deletedAt"
        />
        <FriedH5>{{ createdByMetaData.name || t('dr_chat_removed_participant') }}</FriedH5>
      </div>

      <div v-if="!readOnly" class="controls">
        <div v-if="!isRead" class="blue-dot" />
        <FriedIconButton
          v-if="isResolvable"
          v-tooltip="{
            content: t('cc_tooltip_mark_as_resolved'),
            distance: 10,
            delay: { show: 300, hide: 100 },
          }"
          data-external="comment-resolve-button"
          @click.stop="$emit('resolve')"
        >
          <FriedIcon class="resolve-check-mark" icon="bold-check-mark" />
        </FriedIconButton>
      </div>
    </div>
    <div :class="['message', { 'is-reply': isReply }]">
      <EditorContent :class="['message-text', { collapse: !expanded }]" :editor="editor" />
      <MessageCreatedAt :created-at="createdAt" />
    </div>
    <ReadsIndicator v-if="readEvents.length" :read-events="readEvents" />
  </div>
</template>
<script lang="ts">
import { EnterBreakNode, InputDocNode, ParagraphNode, MentionNode } from '@getaccept/editor-lib';
import { AvatarSize } from '@getaccept/fried-tofu';
import History from '@tiptap/extension-history';
import Text from '@tiptap/extension-text';
import { Editor, EditorContent } from '@tiptap/vue-3';
import type { PropType, Ref } from 'vue';
import { computed, defineComponent, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type { MentionParticipant } from '@getaccept/lib-shared/src/contextual-commenting/mentions/types/mention-participant';
import type { CommentParticipantMetaData } from '../types/comment-participant-meta-data';
import Avatar from '../../components/Avatar.vue';
import MessageCreatedAt from './MessageCreatedAt.vue';
import ReadsIndicator from './ReadsIndicator.vue';

export default defineComponent({
  components: {
    EditorContent,
    ReadsIndicator,
    MessageCreatedAt,
    Avatar,
  },
  props: {
    createdAt: {
      type: Number,
      default: 0,
    },
    isRead: {
      type: Boolean,
      default: false,
    },
    isReply: {
      type: Boolean,
      default: false,
    },
    isResolvable: {
      type: Boolean,
      default: false,
    },
    createdByMetaData: {
      type: Object as PropType<CommentParticipantMetaData>,
      required: true,
    },
    text: {
      type: String,
      default: '',
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    expanded: {
      type: Boolean,
      default: true,
    },
    readEvents: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    mentionParticipants: {
      type: Array as PropType<MentionParticipant[]>,
      default: () => [],
    },
    isRemovedParticipant: {
      type: Boolean,
    },
  },
  emits: ['line-count', 'resolve'],
  setup: (props, { emit }) => {
    const { t } = useI18n();
    const editor: Ref<Editor | null> = ref(null);

    const parsedText = computed(() => {
      // TODO: Delete all comments with plain text in DB, remove this try catch
      let content;
      try {
        content = JSON.parse(props.text);
      } catch (e) {
        content = props.text;
      }

      return content || '';
    });

    const getMessageLineCount = () => {
      const editorRef = editor.value.view.dom;

      const tempElement = document.createElement('div');
      tempElement.style.position = 'absolute';
      tempElement.style.visibility = 'hidden';
      tempElement.style.wordBreak = 'break-word';
      tempElement.style.width = `${editorRef.clientWidth}px`;
      tempElement.innerText = editorRef.innerText.trimEnd();

      document.body.appendChild(tempElement);

      const brCount = tempElement.querySelectorAll('br').length;
      const range = document.createRange();
      range.selectNodeContents(tempElement);
      const lineCount = range.getClientRects().length;

      document.body.removeChild(tempElement);

      return lineCount - brCount;
    };

    watch(parsedText, () => {
      if (editor.value) {
        editor.value.commands.setContent(parsedText.value);
      }
    });

    onMounted(async () => {
      editor.value = new Editor({
        content: parsedText.value,
        editable: false,
        enablePasteRules: false,
        extensions: [
          InputDocNode,
          ParagraphNode,
          Text,
          History,
          EnterBreakNode,
          MentionNode.configure({ mentionParticipants: props.mentionParticipants }),
        ],
        onCreate: () => {
          emit('line-count', getMessageLineCount());
        },
      });
    });

    return {
      AvatarSize,
      editor,
      t,
    };
  },
});
</script>
<style lang="scss" scoped>
@import '../../scss/removed-participant';

$dot-size: 8px;
@mixin display-flex {
  display: flex;
  align-items: center;
}

.collapse {
  /* stylelint-disable-next-line value-no-vendor-prefix */
  display: -webkit-box;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: break-word;
}

.message-container {
  display: flex;
  flex-direction: column;

  .header,
  .message {
    @include display-flex;

    justify-content: space-between;
    gap: var(--spacing-50);
  }

  .header {
    height: 40px;

    .comment-author {
      @include display-flex;

      .avatar {
        margin-right: 8px;
      }
    }

    .controls {
      @include display-flex;

      height: 40px;

      .blue-dot {
        width: $dot-size;
        height: $dot-size;
        border-radius: 50%;
        background: var(--blue-45);
      }

      .resolve-check-mark {
        color: var(--info-dark);
      }
    }
  }

  .message {
    @include display-flex;

    min-height: 32px;

    &.is-reply {
      margin-left: 40px;
    }

    .message-text {
      /* stylelint-disable-next-line selector-class-pattern */
      :deep(.ProseMirror p) {
        font-family: var(--default-font-family);
        font-weight: var(--paragraph-small-font-weight);
        line-height: var(--paragraph-small-line-height);
        margin-block-start: 0;
        margin-block-end: 0;
        color: var(--text-dark);
        overflow-wrap: anywhere;
      }
    }
  }
}
</style>
